// Global Error Logging System for CIW Integration

export interface ErrorLogEntry {
  id: string
  timestamp: Date
  message: string
  type: 'error' | 'warning' | 'info'
  source?: string
  details?: any
}

type ErrorLogListener = (entry: ErrorLogEntry) => void

// Field names whose values should never be retained in client-side logs.
const SENSITIVE_FIELDS = new Set([
  'password',
  'newPassword',
  'oldPassword',
  'currentPassword',
  'credentials',
  'authToken',
  'token',
  'jwt',
  'secret',
  'apiKey',
  'cardNumber',
  'cvv',
  'ssn',
  'medicalConditions',
  'medicalNotes',
  'medicalInfo',
  'dateOfBirth',
])

const REDACTED = '[REDACTED]'

export function redactSensitive(value: unknown, depth = 0): unknown {
  if (value == null || depth > 4) return value
  if (Array.isArray(value)) return value.map(v => redactSensitive(v, depth + 1))
  if (typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_FIELDS.has(k)) {
      out[k] = REDACTED
    } else {
      out[k] = redactSensitive(v, depth + 1)
    }
  }
  return out
}

class ErrorLogger {
  private listeners: ErrorLogListener[] = []
  private logs: ErrorLogEntry[] = []

  constructor() {
    // Capture global JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError(
        `JavaScript Error: ${event.message}`,
        'error',
        'Global',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error?.stack
        }
      )
    })

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(
        `Unhandled Promise Rejection: ${event.reason}`,
        'error',
        'Promise',
        { reason: event.reason }
      )
    })

    // Capture console errors (override console.error)
    const originalConsoleError = console.error
    console.error = (...args) => {
      this.logError(
        `Console Error: ${args.join(' ')}`,
        'error',
        'Console',
        { args }
      )
      originalConsoleError.apply(console, args)
    }
  }

  /**
   * Add a listener for error log entries (used by CIW)
   */
  addListener(listener: ErrorLogListener): () => void {
    this.listeners.push(listener)
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  /**
   * Log an error entry
   */
  logError(
    message: string, 
    type: 'error' | 'warning' | 'info' = 'error',
    source?: string,
    details?: any
  ): void {
    const entry: ErrorLogEntry = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      message: this.formatMessage(message, source),
      type,
      source,
      details: redactSensitive(details)
    }

    this.logs.push(entry)
    
    // Limit log history to prevent memory issues
    if (this.logs.length > 1000) {
      this.logs = this.logs.slice(-500)
    }

    // Notify all listeners (CIW console)
    this.listeners.forEach(listener => {
      try {
        listener(entry)
      } catch (err) {
        // Prevent infinite error loops
        console.warn('Error in error log listener:', err)
      }
    })
  }

  /**
   * Log API errors with context
   */
  logAPIError(
    endpoint: string,
    method: string,
    status: number,
    message: string,
    details?: any
  ): void {
    this.logError(
      `API ${method} ${endpoint} failed (${status}): ${message}`,
      'error',
      'API',
      { endpoint, method, status, ...details }
    )
  }

  /**
   * Log user action errors
   */
  logUserActionError(
    action: string,
    component: string,
    message: string,
    details?: any
  ): void {
    this.logError(
      `User action "${action}" in ${component} failed: ${message}`,
      'error',
      'UserAction',
      { action, component, ...details }
    )
  }

  /**
   * Log STL processing errors
   */
  logSTLError(
    operation: string,
    filename: string,
    message: string,
    details?: any
  ): void {
    this.logError(
      `STL ${operation} failed for "${filename}": ${message}`,
      'error',
      'STL',
      { operation, filename, ...details }
    )
  }

  /**
   * Log warnings
   */
  logWarning(message: string, source?: string, details?: any): void {
    this.logError(message, 'warning', source, details)
  }

  /**
   * Log info messages
   */
  logInfo(message: string, source?: string, details?: any): void {
    this.logError(message, 'info', source, details)
  }

  /**
   * Format error message with source
   */
  private formatMessage(message: string, source?: string): string {
    if (source) {
      return `[${source}] ${message}`
    }
    return message
  }

  /**
   * Get all logs (for debugging)
   */
  getAllLogs(): ErrorLogEntry[] {
    return [...this.logs]
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = []
  }
}

// Export singleton instance
export const errorLogger = new ErrorLogger()

// Convenience functions for common error types
export const logError = (message: string, source?: string, details?: any) => 
  errorLogger.logError(message, 'error', source, details)

export const logWarning = (message: string, source?: string, details?: any) => 
  errorLogger.logWarning(message, source, details)

export const logInfo = (message: string, source?: string, details?: any) => 
  errorLogger.logInfo(message, source, details)

export const logAPIError = (endpoint: string, method: string, status: number, message: string, details?: any) =>
  errorLogger.logAPIError(endpoint, method, status, message, details)

export const logUserActionError = (action: string, component: string, message: string, details?: any) =>
  errorLogger.logUserActionError(action, component, message, details)

export const logSTLError = (operation: string, filename: string, message: string, details?: any) =>
  errorLogger.logSTLError(operation, filename, message, details)
